/**
 * Activate Sync schedule step — defaults and frequency options.
 * Matches webapp SyncScheduleForm / warehouseSource.getBasicSyncScheduleOptions
 * (`every` in minutes; connection payload shaped at Enable).
 */

import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

/**
 * The wizard's schedule state is exactly what the edit form seeds from: `audience.readActivationConfig`'s
 * `schedule` (`Required<ActivationScheduleInput>` in `src/services/audience-rudderstack.ts`). Derived
 * from the router, not re-declared, so a schedule setting added on the backend reaches the form without
 * being copied here. Type-only, erased at build time.
 */
export type ActivationScheduleConfig = Awaited<
  ReturnType<RouterClient<AppRouter>['audience']['readActivationConfig']>
>['schedule'];

export type YesNo = ActivationScheduleConfig['logsEnabled'];

export type ActivationScheduleType = ActivationScheduleConfig['scheduleType'];

/** rudder-sources keeps failed-record errors at least this long; the webapp's input has the same floor. */
export const ERROR_DETAILS_RETENTION_FLOOR_DAYS = 7;

/**
 * The webapp's consent text for this opt-in, kept word for word (it is under PII review there) except
 * the retention clause — the one part the webapp itself parameterises per surface. Here the days input
 * appears beside the option once it is on, so the clause names that and its default.
 */
export const RETAIN_FAILURES_CONSENT = {
  title: 'Retain and show failures?',
  description:
    'Turning this on stores the error RudderStack recorded for each failed record in your warehouse. This may include values echoed back by the destination as part of that error. This data lives only in your warehouse, for the number of days you set beside this option (30 unless you change it), and capture takes effect from your next sync — records from earlier syncs won’t be captured.',
  confirmLabel: 'Enable',
} as const;

/** warehouseSource basic sync schedule options (minutes → label). */
export const ACTIVATION_FREQUENCY_OPTIONS = [
  { value: 1440, label: 'Every 24 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 360, label: 'Every 6 hours' },
  { value: 180, label: 'Every 3 hours' },
  { value: 60, label: 'Every 1 hour' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 10, label: 'Every 10 minutes' },
  { value: 5, label: 'Every 5 minutes' },
] as const;

export const DEFAULT_BASIC_EVERY_MINUTES = ACTIVATION_FREQUENCY_OPTIONS[0].value;

/**
 * The frequency choices to render, given what the sync currently runs at.
 *
 * The list above is what Lookout OFFERS; it is not the set of cadences that can exist. A sync
 * configured in the RudderStack webapp can run at any interval, and the edit form reads it back
 * normalised to minutes — so `every: 120` (a webapp "every 2 hours") matches no option, and a bare
 * `Select` renders its placeholder as though nothing were set. The value is held correctly and saves
 * back unchanged, but a control that looks unset invites a pick, and picking one changes the schedule.
 *
 * Showing the stored value as its own entry makes the current cadence visible without adding it to
 * what Lookout offers: it appears only while it is the selected value.
 */
export function frequencyOptionsFor(
  everyMinutes: number,
): readonly { readonly value: number; readonly label: string }[] {
  // A non-positive or fractional value is not a cadence anyone chose: `every: 0` is the platform's own
  // marker for a MANUAL sync. Injecting it would turn an obviously-unset control into one reading
  // "Every 0 minutes", which looks deliberate and, if saved, gives rudder-sources an interval of zero.
  // The plain list leaves the Select showing its placeholder, which is the honest rendering.
  if (!Number.isInteger(everyMinutes) || everyMinutes <= 0) return ACTIVATION_FREQUENCY_OPTIONS;
  if (ACTIVATION_FREQUENCY_OPTIONS.some((option) => option.value === everyMinutes)) {
    return ACTIVATION_FREQUENCY_OPTIONS;
  }
  return [{ value: everyMinutes, label: describeCadence(everyMinutes) }, ...ACTIVATION_FREQUENCY_OPTIONS];
}

/** "Every 2 hours" reads better than "Every 120 minutes" for a value that divides cleanly. */
function describeCadence(minutes: number): string {
  // Reached only for values NOT in the list above, so 1440 and 60 never arrive here and the singular
  // day/hour cases are unreachable by construction — the plural forms are the only live ones.
  if (minutes % 1440 === 0) return `Every ${String(minutes / 1440)} days`;
  if (minutes % 60 === 0) return `Every ${String(minutes / 60)} hours`;
  return `Every ${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

export function defaultActivationSchedule(): ActivationScheduleConfig {
  return {
    scheduleType: 'basic',
    every: DEFAULT_BASIC_EVERY_MINUTES,
    startAt: '',
    logsEnabled: 'yes',
    logRetentionInDays: 30,
    snapshotsToRetain: 5,
    enableFailedKeysRetry: 'yes',
    errorDetailsEnabled: 'no',
    errorDetailsRetentionInDays: 30,
  };
}

type ErrorDetailsKey = 'errorDetailsEnabled' | 'errorDetailsRetentionInDays';

/** What an edit sends: the failure-retention fields are optional, and absent leaves the stored value. */
export type ActivationScheduleEdit = Omit<ActivationScheduleConfig, ErrorDetailsKey> &
  Partial<Pick<ActivationScheduleConfig, ErrorDetailsKey>>;

/**
 * The schedule an edit saves. The form is seeded once, so "Retain and show failures" is sent only when
 * the person changed it here. Re-sending the seeded copy would revert a change made in the web app while
 * the form was open (a stale "yes" would turn capture back on after consent was withdrawn).
 */
export function scheduleForEdit(
  current: ActivationScheduleConfig,
  seeded: ActivationScheduleConfig,
): ActivationScheduleEdit {
  const { errorDetailsEnabled, errorDetailsRetentionInDays, ...rest } = current;
  return {
    ...rest,
    ...(errorDetailsEnabled !== seeded.errorDetailsEnabled ? { errorDetailsEnabled } : {}),
    ...(errorDetailsRetentionInDays !== seeded.errorDetailsRetentionInDays ? { errorDetailsRetentionInDays } : {}),
  };
}

export function scheduleTypeChanged(
  current: ActivationScheduleConfig,
  scheduleType: ActivationScheduleType,
): ActivationScheduleConfig {
  if (scheduleType === 'manual') {
    return { ...current, scheduleType, every: 0, startAt: '' };
  }
  return {
    ...current,
    scheduleType,
    every: current.every > 0 ? current.every : DEFAULT_BASIC_EVERY_MINUTES,
  };
}
